import React, { useEffect, useRef } from "react";
import { toast } from "sonner";

import AvisoMesaMudou from "./AvisoMesaMudou";
import ComandaMesa from "./ComandaMesa";
import ConferenciaMesa from "./ConferenciaMesa";
import PedidoDeEntrada from "./PedidoDeEntrada";
import TelaAguardando from "./TelaAguardando";
import TelaEntrada from "./TelaEntrada";
import TelaIdentificacao from "./TelaIdentificacao";
import TelaOutraMesa from "./TelaOutraMesa";
import { itensParaRodada, totalDoCarrinho } from "./carrinho";
import { abrirComprovante } from "./useMesaQr";

// Telas em que o anfitrião pode receber o aviso de "fulano quer entrar".
const TELAS_COM_AVISO = ["cardapio", "conferencia", "comanda"];

// Orquestra as telas do modo mesa por cima do cardápio que já existe. Enquanto
// o cliente navega no cardápio este componente sai da frente: quem mostra a
// mesa e o atalho da comanda é a barra do próprio cardápio. Só o aviso de
// pedido de entrada (pro anfitrião) pode aparecer por cima do cardápio.
export default function MesaQrFluxo({
  mesaQr, cart, produtos, fotoDe, darkMode, nomeLoja, logoLoja, cashbackPct, onRodadaEnviada, onPagar,
}) {
  const {
    ativo, tela, mesa, config, impedimento, cliente, eu, comanda, carregando, enviando,
    participantes, anfitriaoAberto, conviteValido, linkConvite,
  } = mesaQr;
  const statusAnterior = useRef(eu?.status);

  // Marca o modo mesa no body: componentes fundos do cardápio (os chips de
  // entrega no cabeçalho) leem daqui em vez de receber a prop por 4 níveis.
  useEffect(() => {
    if (!ativo) return undefined;
    document.body.dataset.modoMesa = "1";
    return () => { delete document.body.dataset.modoMesa; };
  }, [ativo]);

  // Quem esperava e acabou de ser liberado cai no cardápio com um aviso.
  useEffect(() => {
    if (statusAnterior.current === "pendente" && eu?.status === "aprovado") {
      toast.success(`${participantes?.anfitriao || "A mesa"} liberou sua entrada. Bom apetite!`);
    }
    statusAnterior.current = eu?.status;
  }, [eu?.status, participantes?.anfitriao]);

  if (!ativo) return null;

  if (carregando) {
    return (
      <div className={`fixed inset-0 z-[60] flex items-center justify-center ${darkMode ? "bg-zinc-950 text-zinc-100" : "bg-white text-zinc-900"}`}>
        <span className="text-sm opacity-60">Carregando...</span>
      </div>
    );
  }

  const decidirEntrada = async (alvoId, aprovar) => {
    const r = await mesaQr.decidirEntrada(alvoId, aprovar);
    if (r?.ok) toast.success(aprovar ? "Entrada liberada." : "Entrada recusada.");
    else toast.error(r?.erro?.mensagem || "Não consegui responder agora. Tente de novo.");
    return r;
  };

  const souAnfitriao = eu?.papel === "anfitriao" && eu?.status === "aprovado";
  const aviso = souAnfitriao && TELAS_COM_AVISO.includes(tela) ? (
    <PedidoDeEntrada
      pendentes={participantes?.pendentes} mesa={mesa} fotoDe={fotoDe} darkMode={darkMode}
      onDecidir={decidirEntrada}
    />
  ) : null;

  const chamarGarcom = async () => {
    const r = await mesaQr.chamarGarcom();
    if (r?.ok) toast.success("Avisamos o salão. Já vem alguém!");
    else toast.error(r?.erro?.mensagem || "Não consegui chamar o garçom.");
  };

  // Quem abriu a mesa leva a conta inteira para a mesa deste QR.
  const mudarParaCa = async () => {
    const r = await mesaQr.mudarParaCa();
    if (r?.ok) {
      const comigo = (r.dados?.participantes || []).length > 1;
      toast.success(`Pronto! Sua mesa agora é a ${r.dados?.mesa?.nome || "nova"}.${comigo ? " Avisamos quem está com você." : ""}`);
      return;
    }
    toast.error(r?.erro?.mensagem || "Não deu para mudar de mesa agora. Tente de novo.");
    // A mesa foi ocupada no meio (ou a dele fechou): a tela certa agora é outra
    if (["MESA_OCUPADA", "SESSAO_EXPIRADA"].includes(r?.erro?.codigo)) setTimeout(() => window.location.reload(), 1800);
  };

  let conteudo = null;

  if (tela === "entrada") {
    conteudo = (
      <TelaEntrada
        mesa={mesa} loja={nomeLoja} logo={logoLoja} impedimento={impedimento}
        anfitriao={anfitriaoAberto} convidado={conviteValido} carregando={enviando} darkMode={darkMode}
        onEntrar={mesaQr.entrar}
        minhaMesa={mesaQr.minhaMesa} onVoltarParaMinhaMesa={mesaQr.voltarParaMinhaMesa}
      />
    );
  } else if (tela === "outra_mesa") {
    conteudo = (
      <TelaOutraMesa
        mesa={mesa} minhaMesa={mesaQr.minhaMesa} loja={nomeLoja} logo={logoLoja} darkMode={darkMode}
        mudando={mesaQr.mudando} onMudar={mudarParaCa} onVoltar={mesaQr.voltarParaMinhaMesa}
        onFechar={() => mesaQr.irPara("entrada")}
      />
    );
  } else if (tela === "identificacao") {
    conteudo = (
      <TelaIdentificacao
        darkMode={darkMode}
        cashbackPct={cashbackPct}
        exigeTelefone={!!config?.exige_telefone}
        onVoltar={() => mesaQr.irPara("entrada")}
        onConfirmar={async (cpf, nome, telefone) => {
          const r = await mesaQr.identificar(cpf, nome, telefone);
          if (r?.ok && !r.novo && r.eu?.status === "aprovado") {
            toast.success(`Bem-vindo de volta, ${r.cliente?.nome || ""}!`);
          }
          return r;
        }}
      />
    );
  } else if (tela === "aguardando") {
    conteudo = (
      <TelaAguardando
        mesa={mesa} anfitriao={participantes?.anfitriao || anfitriaoAberto}
        status={eu?.status} darkMode={darkMode}
        chamarGarcom={!!config?.chamar_garcom} chamadoAberto={!!mesa?.chamado_aberto}
        onChamarGarcom={chamarGarcom} onPedirDeNovo={mesaQr.pedirEntradaDeNovo}
      />
    );
  } else if (tela === "conferencia") {
    const total = totalDoCarrinho(cart);
    conteudo = (
      <ConferenciaMesa
        mesa={mesa}
        itens={cart}
        produtos={produtos}
        darkMode={darkMode}
        enviando={enviando}
        cashbackGanho={cashbackPct ? (total * cashbackPct) / 100 : 0}
        onVoltar={() => mesaQr.irPara("cardapio")}
        onEnviar={async () => {
          const r = await mesaQr.enviarRodada(itensParaRodada(cart, produtos));
          if (r?.ok) {
            onRodadaEnviada?.();
            toast.success("Pedido enviado para a cozinha!");
          } else {
            toast.error(r?.mensagem || "Não consegui enviar seu pedido.");
          }
        }}
      />
    );
  } else if (tela === "comanda") {
    conteudo = (
      <ComandaMesa
        mesa={mesa}
        comanda={comanda}
        cliente={cliente}
        config={config}
        darkMode={darkMode}
        participantes={participantes}
        fotoDe={fotoDe}
        onDecidirEntrada={decidirEntrada}
        linkConvite={linkConvite}
        onComprovante={mesaQr.codigoSaida ? () => abrirComprovante(mesaQr.codigoSaida) : null}
        chamadoAberto={!!mesa?.chamado_aberto}
        onAdicionar={() => mesaQr.irPara("cardapio")}
        onPagar={onPagar}
        onChamarGarcom={chamarGarcom}
      />
    );
  }

  return (
    <>
      {conteudo}
      {aviso}
      <AvisoMesaMudou
        texto={mesaQr.avisoMudanca} mesa={mesa} darkMode={darkMode} onFechar={mesaQr.fecharAvisoMudanca}
      />
    </>
  );
}
